/* eslint-disable react-hooks/exhaustive-deps */
import { EditOutlined, PlusOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import { Table, Button, message, Modal, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { useCategoriesCatalog } from "../../../hooks/useCatalogData";
import { parseLocalizedName } from "../../../utils/localization";
import usePermissions from "../../../hooks/usePermissions";
import { filterBySearch } from "../../../shared/lib/listSearch";
import ListSearch from "../../../shared/ui/ListSearch";
import "../../../shared/styles/ErpPages.css";

const GetCategory = () => {
  const { hasPermission } = usePermissions();
  const { t, i18n } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const { data: categoriesDataRaw, loading, refresh } = useCategoriesCatalog();

  useEffect(() => {
    const mappedCategories = categoriesDataRaw.map((item) => ({
      key: item.id,
      id: item.id,
      name: parseLocalizedName(item.name, i18n.language, "N/A"),
      description: item.description || "-",
      status: item.status || "inactive",
    }));
    setDataSource(mappedCategories);
  }, [categoriesDataRaw, i18n.language]);

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmedDelete = async () => {
    try {
      await apiClient.delete(`/categories/${deleteItemId}`);
      message.success(t("Category deleted successfully"));
      await refresh();
    } catch (error) {
      message.error(t("Failed to delete category"));
    } finally {
      setIsDeleteModalVisible(false);
      setDeleteItemId(null);
    }
  };

  const filteredData = useMemo(
    () =>
      filterBySearch(dataSource, searchText, (item) => [
        item.id,
        item.name,
        item.description,
        item.status,
      ]),
    [dataSource, searchText]
  );

  const columns = [
    {
      title: t("id", "ID"),
      dataIndex: "id",
      width: 70,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: t("Status"),
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) =>
        status === "active" ? (
          <span className="erp-status clear">{t(status)}</span>
        ) : (
          <span className="erp-status debt">{t(status)}</span>
        ),
    },
    {
      title: t("action", "Action"),
      key: "actions",
      width: 180,
      fixed: "right",
      render: (_, record) => (
        <Space size={4}>
          {hasPermission("categories.edit") && (
            <Link to={`/categories/update/${record.id}`}>
              <Button size="small" icon={<EditOutlined />}>
                {t("Edit")}
              </Button>
            </Link>
          )}
          {hasPermission("categories.delete") && (
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            >
              {t("Delete")}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="erp-page">
      <div className="erp-shell">
        <div className="erp-header">
          <div>
            <div className="erp-breadcrumb">
              {t("sidebarCatalog", "Catalog")} / {t("categories", "Categories")}
            </div>
            <h1>{t("categories", "Categories")}</h1>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refresh?.()} loading={loading}>
              {t("refresh", "Refresh")}
            </Button>
            {hasPermission("categories.add") && (
              <Link to="/categories/add">
                <Button type="primary" icon={<PlusOutlined />}>
                  {t("addCategory", "New category")}
                </Button>
              </Link>
            )}
          </Space>
        </div>

        <div className="erp-meta">
          <span>
            {t("records", "Records")}: <b>{dataSource.length}</b>
          </span>
        </div>

        <div className="erp-toolbar">
          <ListSearch
            value={searchText}
            onChange={setSearchText}
            placeholder={t("Search categories")}
            width={280}
          />
        </div>

        <div className="erp-body">
          <Table
            size="small"
            loading={loading}
            dataSource={filteredData}
            columns={columns}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (n) => `${n} ${t("records", "records")}`,
            }}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: t("noDataFound", "No records") }}
          />
        </div>
      </div>

      <Modal
        title={t("Confirm Delete")}
        open={isDeleteModalVisible}
        onOk={handleConfirmedDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText={t("Yes")}
        cancelText={t("No")}
        okButtonProps={{ danger: true }}
      >
        <p>{t("Are you sure you want to delete this category?")}</p>
      </Modal>
    </div>
  );
};

export default GetCategory;
